import React from 'react';
import { IAddress } from '../../Api/Models/Order';
import { Checkbox, CheckboxOnChangeData, Field, Input, Label } from '@fluentui/react-components';
import { ILocation } from '../../Api/Models/Customer';
import CountrySelection from "./CountrySelector";
import RegionSelector from "./RegionSelector";
import { useLocalization } from '../Helpers/useLocalization';

const COUNTRY_LABEL_KEYS: Record<string, { state: string; zip: string }> = {
    'United States':  { state: 'localization_address_state',            zip: 'localization_address_zip_code'     },
    'Canada':         { state: 'localization_address_province',         zip: 'localization_address_postal_code'  },
    'Australia':      { state: 'localization_address_state_territory',  zip: 'localization_address_postcode'     },
    'United Kingdom': { state: 'localization_address_county',           zip: 'localization_address_postcode'     },
};
const DEFAULT_LABEL_KEYS = { state: 'localization_address_state_province', zip: 'localization_address_zip_postal_code' };

interface IAddressFormProps {
    address: IAddress;
    label?: string;
    pullingFromLocation?: boolean;
    locationDetails?: ILocation;
    onChange: (address: IAddress) => void;
    //onValidateRequired?: (valid: boolean) => void;
    onCopyFromChange?: (checked: boolean) => void;
    copyFromEnabled?: boolean;
    copyFrom?: IAddress;
    copyFromLabel?: string;
    defaultCountry?: string;
}

const AddressForm: React.FC<IAddressFormProps> = (props) => {
    const L = useLocalization();

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        const updatedAddress = { ...props.address, [name]: value };
        props.onChange(updatedAddress);
    }

    const copyFromChange = (ev: React.ChangeEvent<HTMLInputElement>, data: CheckboxOnChangeData) => {
        if (data.checked && props.copyFrom) {
            props.onChange(props.copyFrom);
        }
        if (props.onCopyFromChange) {
            props.onCopyFromChange(data.checked == true);
        }
    }

    const fieldLabelKeys = COUNTRY_LABEL_KEYS[props.address.country ?? props.defaultCountry ?? ''] ?? DEFAULT_LABEL_KEYS;

    return (
        <div className='address-form'>
            {props.label && <h2>{props.label}</h2>}
            {props.copyFrom && props.copyFromLabel && <Field>
                <Checkbox label={props.copyFromLabel} onChange={copyFromChange} checked={props.copyFromEnabled} />
            </Field>}
            {!props.copyFromEnabled && <>
                <Field>
                    <Label required>{L('localization_address_name')}</Label>
                    <Input
                        name='name'
                        value={props.address.name ?? ''}
                        onChange={handleChange}
                        onBlur={handleChange}
                        disabled={props.copyFromEnabled}
                        placeholder={props.copyFromEnabled ? props.copyFrom?.name : ''}
                        />
                </Field>
                <Field>
                    <Label required>{L('localization_address_country')}</Label>
                    <CountrySelection 
                        value={props.address.country} 
                        onChange={(country) => props.onChange({...props.address, country, state: ''})} 
                        disabled={props.copyFromEnabled || props.pullingFromLocation} 
                        placeholder={props.locationDetails?.country ?? (props.copyFromEnabled ? props.copyFrom?.country : '')}/>
                </Field>
                <Field>
                    <Label required>{L('localization_address_address1')}</Label>
                    <Input
                        required={true}
                        name='address1'
                        value={props.address.address1 ?? ''}
                        onChange={handleChange}
                        onBlur={handleChange}
                        disabled={props.copyFromEnabled || props.pullingFromLocation}
                        placeholder={props.locationDetails?.address ?? (props.copyFromEnabled ? props.copyFrom?.address1 : '')}
                        />
                </Field>
                <Field>
                    <Label>{L('localization_address_address2')}</Label>
                    <Input
                        name='address2'
                        value={props.address.address2 ?? ''}
                        onChange={handleChange}
                        onBlur={handleChange}
                        disabled={props.copyFromEnabled || props.pullingFromLocation}
                        placeholder={props.locationDetails?.address2 ?? (props.copyFromEnabled ? props.copyFrom?.address2 : '')}
                        />
                </Field>

                <div className='address-form-csz'>
                    <Field>
                        <Label required>{L('localization_address_city')}</Label>
                        <Input
                            name='city'
                            value={props.address.city ?? ''}
                            onChange={handleChange}
                            onBlur={handleChange}
                            disabled={props.copyFromEnabled || props.pullingFromLocation}
                            placeholder={props.locationDetails?.city ?? (props.copyFromEnabled ? props.copyFrom?.city : '')}
                            />
                    </Field>
                    <Field>
                        <Label required>{L(fieldLabelKeys.state)}</Label>
                        <RegionSelector
                            country={props.address.country}
                            value={props.address.state}
                            onChange={(state) => props.onChange({ ...props.address, state })}
                            disabled={props.copyFromEnabled || props.pullingFromLocation}
                            placeholder={props.locationDetails?.state ?? (props.copyFromEnabled ? props.copyFrom?.state : '')}
                        />
                    </Field>
                    <Field>
                        <Label required>{L(fieldLabelKeys.zip)}</Label>
                        <Input
                            name='zipCode'
                            value={props.address.zipCode ?? ''}
                            onChange={handleChange}
                            onBlur={handleChange}
                            disabled={props.copyFromEnabled || props.pullingFromLocation}
                            placeholder={props.locationDetails?.postalCode ?? (props.copyFromEnabled ? props.copyFrom?.zipCode : '')}
                        />
                    </Field>
                </div>

                <Field>
                    <Label required>{L('localization_address_phone_number')}</Label>
                    <Input
                        name='phoneNumber'
                        value={props.address.phoneNumber ?? ''}
                        onChange={handleChange}
                        onBlur={handleChange}
                        disabled={props.copyFromEnabled}
                        placeholder={props.copyFromEnabled ? props.copyFrom?.phoneNumber : ''}
                        />
                </Field>
            </>}
        </div>
    )
}

export default AddressForm;

